import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, Mail, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message envoyé !");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24" data-testid="section-contact">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="rounded-[36px] bg-[#1A1A1A] text-white p-10 lg:p-16 grid lg:grid-cols-12 gap-10 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[#C68E59]/25 blur-3xl" />
          <div className="lg:col-span-5 relative">
            <p className="caption-label text-[#E6C29A]">À la recherche de talents ?</p>
            <h2 className="font-serif text-4xl lg:text-5xl mt-3 leading-tight">
              Recrutez <span className="italic text-[#D4A574]">autrement</span>.
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed text-sm">
              Des créateurs choisis pour leur univers, pas leur CV. Parlez-nous de votre projet — réponse sous 48 h ouvrées.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              <a href="mailto:hello@helencia.fr" className="flex items-center gap-3 text-white/85 hover:text-[#E6C29A] transition">
                <Mail className="w-4 h-4 text-[#D4A574]" /> hello@helencia.fr
              </a>
              <p className="flex items-center gap-3 text-white/65">
                <Trophy className="w-4 h-4 text-[#D4A574]" /> Concours / partenariats : partners@helencia.fr
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            {sent ? (
              <div className="bg-white rounded-[24px] p-10 text-center" data-testid="contact-success">
                <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-[#D4A574]" />
                </div>
                <p className="font-serif text-2xl text-[#1A1A1A]">Message envoyé.</p>
                <p className="text-sm text-[#6E4B2A] mt-2">Nous reviendrons vers vous très vite.</p>
                <Button onClick={() => setSent(false)} variant="outline" className="mt-6 rounded-full border-[#1A1A1A]" data-testid="contact-new">
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-[24px] p-7 lg:p-8 space-y-4 text-[#1A1A1A]" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="caption-label">Nom</Label>
                    <Input value={form.name} onChange={update("name")} required placeholder="Sophie Moreau" className="rounded-xl mt-2 h-11" data-testid="contact-name" />
                  </div>
                  <div>
                    <Label className="caption-label">Email</Label>
                    <Input type="email" value={form.email} onChange={update("email")} required placeholder="vous@email.com" className="rounded-xl mt-2 h-11" data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <Label className="caption-label">Sujet</Label>
                  <Input value={form.subject} onChange={update("subject")} required placeholder="Collaboration, partenariat…" className="rounded-xl mt-2 h-11" data-testid="contact-subject" />
                </div>
                <div>
                  <Label className="caption-label">Message</Label>
                  <Textarea value={form.message} onChange={update("message")} required rows={5} minLength={10} placeholder="Parlez-nous de votre projet…" className="rounded-xl mt-2" data-testid="contact-message" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12" data-testid="contact-submit">
                  {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>) : "Envoyer"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

